import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import { useBreakpoint } from "../WidgetBreakpoint";
import { useTranslation } from "react-i18next";
import parse from "html-react-parser";

import { Colours } from "@learningblocks/shared-components";

import { makeStyles } from "@material-ui/core/styles";
import Slider from "@material-ui/core/Slider";
import { createMuiTheme } from "@material-ui/core/styles";
import { ThemeProvider } from "@material-ui/styles";

import BarChart2 from "./BarChart2";

export default function Widget(props) {
  const {
    t,
    // i18n
  } = useTranslation("translation");

  // Include the widget providers
  const breakpoints = useBreakpoint();

  const [bins, setBins] = useState(8);
  const [display, setDisplay] = useState("histogram");
  const [loaded, setLoaded] = useState();

  const [widgetArea] = useState({
    height: 0,
    width: 0,
  });

  useEffect(() => {
    import(`./locales/${props.i18next.language}/translation.json`).then(
      (module) => {
        props.i18next.addResourceBundle(
          "en",
          "translation",
          module.default,
          true,
          true
        );
        setLoaded(true);
      }
    );
  }, []);

  const useStyles = makeStyles((theme) => ({
    root: {
      width: "calc(100% - 40px)",
      margin: "auto",
    },
    margin: {
      height: theme.spacing(3),
    },
  }));

  const muiTheme = createMuiTheme({
    overrides: {
      MuiSlider: {
        thumb: {
          height: "1px",
          width: "1px",
          color: "#ffaa27",
        },
        track: {
          height: "5px",
          color: "#ffaa27",
        },
        rail: {
          height: "5px",
          color: "white",
        },
        mark: {
          height: "8px",
          color: "white",
        },
        markLabel: {
          color: "white",
          fontSize: "12px",
          marginLeft: "1px",
        },
        markLabelActive: {
          color: "white",
        },
        valueLabel: {
          top: 7,
          left: -9,
          background: "#ffaa27",
          borderRadius: "10px",
          height: "20px",
          "& *": {
            marginTop: -4,
            marginLeft: 1,
            background: "transparent",
            color: "#003359",
          },
        },
      },
    },
  });

  function updateBins(value) {
    setBins(value);
  }

  const marks = [
    { value: 1, label: "1" },
    { value: 2, label: "" },
    { value: 3, label: "3" },
    { value: 4, label: "" },
    { value: 5, label: "5" },
    { value: 6, label: "" },
    { value: 7, label: "7" },
    { value: 8, label: "" },
    { value: 9, label: "9" },
    { value: 10, label: "" },
    { value: 11, label: "11" },
    { value: 12, label: "" },
    { value: 13, label: "13" },
    { value: 14, label: "" },
    { value: 15, label: "15" },
    { value: 16, label: "" },
    { value: 17, label: "17" },
    { value: 18, label: "" },
    { value: 19, label: "19" },
    { value: 20, label: "" },
  ];

  const smallMarks = [
    { value: 1, label: "1" },
    { value: 3, label: "3" },
    { value: 5, label: "5" },
    { value: 7, label: "7" },
    { value: 9, label: "9" },
    { value: 11, label: "11" },
    { value: 13, label: "13" },
    { value: 15, label: "15" },
    { value: 17, label: "17" },
    { value: 19, label: "19" },
    { value: 20, label: "" },
  ];

  const classes = useStyles();

  const setDisplayTab = (tab) => {
    setDisplay(tab);
  };

  return (
    <>
      <div
        style={{
          display: props.intro === "" ? "none" : null,
          border: "1px solid white",
          borderRadius: "5px",
          padding: "20px",
          color: "white",
          margin: "20px",
        }}
      >
        {parse(t(props.intro))}
      </div>
      <div
        className="widgetWrapper"
        style={{
          width: "100%",
          overflow: "hidden",
          display: loaded ? "flex" : "none",
          flexDirection: "column",
          // border: "1px solid red",
        }}
      >
        <div
          className="altTxt field"
          aria-label={t(
            "A histogram with a sliding bar to adjust the number of bins is shown. The x axis plots the height of the sunflowers in meters, and the y axis plots the frequency or count. A vertical line representing the 3 meter average height for sunflowers is shown over the histogram for comparison. Choosing more bins results in a more detailed view. As you drag the slider to the right, the number of bins increases, the bin width decreases, and the frequency within a bin generally decreases. As you drag the slider to the left, the number of bins decreases, the bin width increases, and the frequency within a bin generally increases. Fewer bins tend to communicate less information about the data but can be better for getting an overall impression of the data. More bins tend to communicate more information about the data, but sometimes the extra detail can obscure the overall impression."
          )}
          style={{
            height: "0px",
            overflow: "hidden",
            // font: "none",
          }}
        >
          {t(
            "A histogram with a sliding bar to adjust the number of bins is shown. The x axis plots the height of the sunflowers in meters, and the y axis plots the frequency or count. A vertical line representing the 3 meter average height for sunflowers is shown over the histogram for comparison. Choosing more bins results in a more detailed view. As you drag the slider to the right, the number of bins increases, the bin width decreases, and the frequency within a bin generally decreases. As you drag the slider to the left, the number of bins decreases, the bin width increases, and the frequency within a bin generally increases. Fewer bins tend to communicate less information about the data but can be better for getting an overall impression of the data. More bins tend to communicate more information about the data, but sometimes the extra detail can obscure the overall impression."
          )}
        </div>
        <div
          className="ctrl-title"
          style={{
            // fontWeight: "bold",
            width: "100%",
            textAlign: "center",
            color: "white",
            padding: "16px 0px 5px 0px",
          }}
        >
          {t("Choose the number of bins")}
        </div>
        <div
          className="chart-type-select"
          style={{
            background: "#002F52",
            maxWidth: "1000px",
            margin: "auto",
            border: "1px solid " + Colours.navy4,
            boxSizing: "border-box",
            borderRadius: "5px",
            display: widgetArea.height < 678 ? "flex" : "none",
            flexDirection: "row",
            width: "calc(100% - 20px)",
          }}
        >
          <div className={classes.root}>
            <ThemeProvider theme={muiTheme}>
              <Slider
                min={1}
                max={20}
                defaultValue={8}
                getAriaValueText={updateBins}
                aria-labelledby="discrete-slider-custom"
                step={1}
                marks={breakpoints.xs || breakpoints.sm ? smallMarks : marks}
                valueLabelDisplay="on"
              />
            </ThemeProvider>
          </div>
        </div>
        <div
          // ref={widget.widgetArea}
          className="widgetCharts"
          style={{
            position: "relative",
            display: "flex",
            width: breakpoints.md ? "calc(100% - 32px)" : "calc(100% - 96px)",
            maxWidth: "1000px",
            height: "calc(100% - 32px)",
            padding: breakpoints.md ? "16px" : "32px",
            overflowY: "hidden",
            alignItems: "center",
            flexDirection: "column",
            justifyContent: "space-around",
            margin: "0 auto",
            // border: "1px solid red",
          }}
        >
          <BarChart2
            width={
              breakpoints.xs
                ? 200
                : breakpoints.sm
                  ? 250
                  : breakpoints.md
                    ? 500
                    : 500
            }
            height={
              breakpoints.xs
                ? 250
                : breakpoints.sm
                  ? 250
                  : breakpoints.md
                    ? 300
                    : 300
            }
            widgetData={props.widgetData}
            bins={bins}
            visible={props.visible}
            display={display}
            setDisplay={setDisplayTab}
          />
        </div>
      </div>
    </>
  );
}

Widget.propTypes = {
  widgetData: PropTypes.object,
  inline: PropTypes.boolean,
  visible: PropTypes.boolean,
  usersnap: PropTypes.string,
};
